import VideoPlayer from "@dana/Components/VideoPlayer";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import { headings } from "@dana/MobiusOverrides/styles/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Badge, { BadgeTypeValues } from "@insite/mobius/Badge";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useState } from "react";
import { css } from "styled-components";

interface LegalDisclaimerScreenProps {
    fields: any;
}

export interface LegalDisclaimerStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    disclaimerVideoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const legalDisclaimerStyles: LegalDisclaimerStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 70%;
            gap: 24px;
            margin: 18px auto 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                        width: 100%;
                                    `,
                        css`
                                        width: 100%;
                                    `,
                        css`
                                        width: 100%;
                                    `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    heading: {
        variant: "h3",
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            gap: 16px;
            border: 1px solid ${themeBackgrounds.lightGray};
            border-radius: 4px;
            padding: 16px;
            box-sizing: border-box;
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            gap: 16px;
        `,
    },
    disclaimerVideoModal: {
        cssOverrides: {
            modalContent: css`
            video {
                width: 100%;
                height: auto;
                max-height: 70vh;
                display: block;
            }
        `,
        },
        sizeVariant: "large",
    },
    watchVideoButton: {
        variant: "primary",
        icon: {
            position: "left",
            src: () => <Youtube />
        },
    },
};

const LegalDisclaimerScreen: React.FC<LegalDisclaimerScreenProps> = ({
    fields,
}) => {
    const styles = legalDisclaimerStyles;
    const [disclaimerVideoModalIsOpen, setDisclaimerVideoModalIsOpen] = useState(false);
    const disclaimerVideoUrl = useGetLink(fields.legalDisclaimerVideo);
    const disclaimerVideoCaption = useGetLink(fields.legalDisclaimerVideoCaption);
    const selectReadyShaftPage = useGetLink(fields.selectReadyShaftPage);
    const { isInShell } = useShellContext();
    const history = useHistory();


    const disclaimerVideoModalCloseHandler = () => {
        setDisclaimerVideoModalIsOpen(false);
    };

    const stopWizard = () => { window.location.href = "/"; };

    const onContinueClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography {...styles.heading} as="h3">
                    {fields.legalDisclaimerHeading}
                </Typography>
                <StyledWrapper {...styles.contentWrapper}>
                    {fields.legalDisclaimerText && (
                        <Typography className="text-body-lg">
                            {parse(fields.legalDisclaimerText, parserOptions)}
                        </Typography>
                    )}
                    {disclaimerVideoUrl?.url && (
                        <Button {...styles.watchVideoButton} onClick={() => setDisclaimerVideoModalIsOpen(true)}>
                            {fields.watchVideoLabel}
                        </Button>
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Button variant="secondary" onClick={() => stopWizard()}>
                            {fields.stopLabel}
                        </Button>
                        <Button variant="primary" onClick={() => onContinueClick(selectReadyShaftPage?.url)}>
                            {fields.continueLabel}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>

            </StyledWrapper>
            <Modal
                {...styles.disclaimerVideoModal}
                id="legalDisclaimerVideoModal"
                isOpen={disclaimerVideoModalIsOpen}
                handleClose={disclaimerVideoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={disclaimerVideoUrl?.url || ''}
                        videoCaptionUrl={disclaimerVideoCaption?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    )
};

export default LegalDisclaimerScreen;